/**
 * Gleap chat widget.
 *
 * Configured from Admin → Settings → Integrations → Gleap Chat Widget
 * (`gleap*` keys in /api/settings.php). The estimator stores nothing: chats
 * live in the Gleap project, and the trusscore.com Gleap webhook receiver
 * (MODX `gleap` package) delivers each chat lead to Pardot exactly once,
 * tagging the source by page host. Never call Gleap.identify or push chat
 * data to the dataLayer here — see the no-PII rule in utils/gtmTracking.ts.
 *
 * The loader is a port of the MODX package chunk `gleap-chat-widget.tpl`.
 * The SDK is loaded once per page; after that, routes that should not show
 * the widget hide the launcher instead of unloading the script.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSettings } from '../context/AppSettingsContext';
import { usePartnerBranding } from '../context/PartnerBrandingContext';

type GleapApi = {
  invoked?: boolean;
  initialize: (key: string) => void;
  on: (event: string, cb: () => void) => void;
  setCustomData: (key: string, value: string) => void;
  setLanguage: (lang: string) => void;
  showFeedbackButton: (show: boolean) => void;
};

declare global {
  interface Window {
    Gleap?: GleapApi;
    GleapActions?: { e: string; a: unknown[] }[];
    clarity?: (...args: unknown[]) => void;
  }
}

/** Always hidden, regardless of the admin's excluded paths. */
const ALWAYS_EXCLUDED = ['/admin'];

function pathMatches(pathname: string, prefix: string): boolean {
  if (prefix === '/') return true;
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

/**
 * Install the Gleap proxy stub + async SDK script and initialize it. Calls made
 * before the SDK arrives are queued in window.GleapActions and replayed by it.
 */
function loadGleap(sdkKey: string, clarityLink: boolean): void {
  if (window.Gleap?.invoked) return;
  window.GleapActions = [];
  const stub = new Proxy({ invoked: true } as Record<string, unknown>, {
    get(target, name) {
      if (name === 'invoked') return target.invoked;
      return (...args: unknown[]) => {
        window.GleapActions!.push({ e: String(name), a: args });
      };
    },
    set(target, name, value) {
      target[name as string] = value;
      return true;
    },
  });
  window.Gleap = stub as unknown as GleapApi;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://sdk.gleap.io/latest/index.js';
  document.head.appendChild(script);
  window.Gleap.initialize(sdkKey);

  if (clarityLink) {
    // Attach the Clarity recording URL on widget open so it reflects the
    // session that was active when the visitor started the chat.
    window.Gleap.on('open', () => {
      if (typeof window.clarity !== 'function') return;
      try {
        window.clarity('url', (url: string) => {
          if (url) window.Gleap?.setCustomData('claritySession', url);
        });
      } catch {
        /* clarity not ready */
      }
    });
  }
}

export const GleapChat: React.FC = () => {
  const { settings, isLoading } = useAppSettings();
  const { partner } = usePartnerBranding();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const loadedRef = useRef(false);

  const configured = settings.gleapEnabled && settings.gleapSdkKey !== '';
  const excluded = [...ALWAYS_EXCLUDED, ...settings.gleapExcludePaths].some(p => pathMatches(pathname, p));
  const blockedOnPartner = partner !== null && !settings.gleapPartnerPortals;
  const show = configured && !excluded && !blockedOnPartner;

  // Load on the first route that should show it; never load otherwise.
  useEffect(() => {
    if (isLoading || !show || loadedRef.current) return;
    loadGleap(settings.gleapSdkKey, settings.gleapClarityLink);
    loadedRef.current = true;
  }, [isLoading, show, settings.gleapSdkKey, settings.gleapClarityLink]);

  // Once loaded, hide/show the launcher as the visitor moves between routes
  // (or an admin toggles the widget off in the same tab).
  useEffect(() => {
    if (!loadedRef.current || !window.Gleap) return;
    window.Gleap.showFeedbackButton(show);
  }, [show]);

  useEffect(() => {
    if (!loadedRef.current || !window.Gleap || !settings.gleapSyncLanguage) return;
    const lang = (i18n.language || 'en').split('-')[0];
    window.Gleap.setLanguage(lang);
  }, [show, settings.gleapSyncLanguage, i18n.language]);

  return null;
};
